import React, { useEffect, useState, useRef } from "react"
import { OverlayPanel } from "../../../../components/atoms/OverlayPanel.atom"
import { InputCheckbox } from "../../../../components/atoms/InputCheckbox.atom"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import PrimaryButton from "../../../../components/molecules/buttons/PrimaryButton.molecule"
import Frame from "../../../../design/assests/images/icons/Frame.svg"
import SecondaryButton from "../../../../components/molecules/buttons/SecondaryButton.molecule"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import { numberFormatter } from "../../../../utils/numbers.utils"
import { displayValueOrPlaceholder } from "../../../../utils/strings.utils"
import {
  getValidLuxonDate,
  generateDateAndTimeNow,
  formatDateRange,
} from "../../../../utils/date.utils"
import {
  CLIENT_SPRINT_STATES,
  NON_TOGGLEABLE_SECTIONS,
  CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY,
} from "../../../../consts/modules/clientReport.consts"
import { sleep } from "../../../../utils/common.utils"
import {
  getSprintReportDocuments,
  getDownloadClientReportSprintSummaryNew,
  createSprintReportDocument,
} from "../../../../services/clientReport.service"
import { generateClientReportPdf } from "../../pdf/generateClientReportPdf"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import { useIsAdmin } from "../../../../hooks/auth/useIsAdmin.hook"
import { useIsTeamLeader } from "../../../../hooks/auth/useIsTeamLeader.hook"
import SplitButton from "../../../../components/atoms/SplitButton.atom"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import useToast from "../../../../hooks/useToast.hook"
import Tooltip from "../../../../components/atoms/Tooltip.atom"

const ClientReportSprintSummary = ({
  sprintData,
  refresh,
  visibleMap,
  onToggleSection,
  getSectionLabel,
  chartRefs,
  hideButtons,
  hideResponsible = true,
  period,
  isPresentationMode = false,
  hookLoading = false,
  collapsible = false,
  extraData,
}) => {
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [data, setData] = useState({})
  const overlayRef = useRef(null)
  const [versions, setVersions] = useState([])
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const isAdmin = useIsAdmin()
  const isTeamLeader = useIsTeamLeader()
  const canGenerateReportVersions = isAdmin || isTeamLeader
  const splitButtonRef = useRef(null)
  const { setSuccessMessage, setErrorMessage } = useToast()

  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)

  useEffect(() => {
    if (isPresentationMode) return setData(sprintData)
    fetchClientPeriod()
  }, [sprintData, refresh, isPresentationMode])

  useEffect(() => {
    if (!canGenerateReportVersions || !sprintData?.id) return

    fetchVersions()
  }, [sprintData?.id])

  const fetchClientPeriod = async () => {
    setLoading(true)
    try {
      if (!sprintData?.id) {
        setData({
          ...sprintData,
          startDate: null,
          state: CLIENT_SPRINT_STATES.IN_PROGRESS,
        })
        return
      }

      const response = await getDownloadClientReportSprintSummaryNew(
        sprintData.id,
        period.toUpperCase(),
        sprintData.clientId
      )

      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result || {}
      setData({
        ...sprintData,
        ...result,
      })
    } catch (error) {
      console.error("Error fetching sprint summary:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchVersions = async () => {
    try {
      setLoadingVersions(true)

      const response = await getSprintReportDocuments(sprintData.id)

      if (!response.success) throw new Error(response.message)

      setVersions(response.result || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingVersions(false)
    }
  }

  const getPeriodLabel = (startDate, endDate) => {
    if (!startDate) return "En curso"
    if (!endDate)
      return `${getValidLuxonDate(startDate)?.setLocale("es").toFormat("dd MMM yyyy")} - En curso`
    return formatDateRange(startDate, endDate)
  }
  const { clientReportSelectedClientName, clientReportSelectedClientLogo } =
    useReportFiltersContext()

  const getReportCreatedAt = () => {
    const createdAt = generateDateAndTimeNow()
    return formatDateRange(createdAt, createdAt)
  }

  const handleDownloadReport = async () => {
    if (downloading) return
    try {
      setDownloading(true)
      // Some charts include animations. When exporting the report to PDF,
      // we wait briefly to ensure the animations finish rendering
      // before capturing the chart DOM.
      await sleep(1000)

      const reportCreatedAt = getReportCreatedAt()

      await generateClientReportPdf({
        sprintData,
        visibleMap,
        clientName: clientReportSelectedClientName,
        clientLogo: clientReportSelectedClientLogo,
        chartRefs,
        reportCreatedAt,
        period,
        extraData,
      })
    } catch (error) {
      console.error("Error generating PDF:", error)
    } finally {
      setDownloading(false)
    }
  }

  const handleGenerateVersion = async () => {
    if (downloading) return

    try {
      setDownloading(true)

      await sleep(1000)
      const reportCreatedAt = getReportCreatedAt()

      const pdfVersion = await generateClientReportPdf({
        sprintData,
        visibleMap,
        clientName: clientReportSelectedClientName,
        clientLogo: clientReportSelectedClientLogo,
        chartRefs,
        period,
        mode: "version",
        reportCreatedAt,
        extraData,
      })

      const formData = new FormData()
      formData.append("document", pdfVersion, "Sprint_Report.pdf")
      formData.append("sprintId", sprintData.id)
      formData.append("period", period.toUpperCase())

      const response = await createSprintReportDocument(formData)
      if (!response.success) throw new Error(response.message)

      await fetchVersions()

      window.open(response.result, "_blank")
      setSuccessMessage({
        title: "¡Todo bien!",
        message: "Nueva versión del reporte generada",
        lifeTime: 5000,
      })
      setShowGenerateDialog(false)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: "No se pudo generar la nueva versión",
        lifeTime: 3000,
      })
    } finally {
      setDownloading(false)
      setShowGenerateDialog(false)
    }
  }
  const handleDownloadLatest = () => {
    if (!versions.length) return

    window.open(versions[0].documentUrl, "_blank")
    setShowDownloadDialog(false)
  }

  const adminMenu =
    showHistory ?
      versions.map((version) => ({
        template: () => {
          const versionLabel = `Sprint ${sprintData?.sprintNumber || ""} v${version.version}`
          return (
            <a
              className="version-menu-item"
              onClick={() => {
                if (version.documentUrl) {
                  window.open(version.documentUrl, "_blank")
                }
              }}
            >
              <i className="pi pi-file-pdf version-menu-icon"></i>

              <div className="version-menu-content">
                <div className="version-menu-title">{versionLabel}</div>
                <div className="version-menu-date">
                  {version.createdAt ?
                    getValidLuxonDate(version.createdAt)
                      .toLocal()
                      .toFormat("dd/MM/yy HH:mm")
                  : "Fecha no disponible"}
                </div>
              </div>

              <Tooltip
                target=".version-menu-download-icon"
                content="Descargar versión"
                position="top"
              />

              <i className="pi pi-download version-menu-download-icon"></i>
            </a>
          )
        },
      }))
    : [
        {
          label: "Generar nueva versión",
          icon: "pi pi-refresh",
          command: () => setShowGenerateDialog(true),
        },
        {
          label: "Historial de versiones",
          icon: "pi pi-clock",
          command: () => {
            setShowHistory(true)
            setTimeout(() => {
              splitButtonRef.current?.show()
            }, 0)
          },
        },
      ]
  const getReportTitle = () => {
    return (
      <div className="summary-report-title-container">
        <div className="summary-report-main-title">{data.title}</div>
        <div className="summary-report-subtitle">{data.subtitle}</div>
      </div>
    )
  }
  return (
    <>
      <ModalForm
        visible={showDownloadDialog}
        setVisible={setShowDownloadDialog}
        headerIconClass="pi pi-info-circle"
        headerTitle="Descargar última versión"
        footer={
          <div>
            <SecondaryButton
              label="Cancelar"
              onClick={() => setShowDownloadDialog(false)}
              rounded
            />
            <PrimaryButton
              rounded
              icon={`pi ${downloading ? "pi-spin pi-spinner" : "pi-check"}`}
              label="Descargar"
              onClick={handleDownloadLatest}
            />
          </div>
        }
      >
        <span>
          Si realizaste cambios importantes recuerda generar una versión nueva
          actualizada.
        </span>
      </ModalForm>
      <ModalForm
        visible={showGenerateDialog}
        setVisible={setShowGenerateDialog}
        headerIconClass="pi pi-info-circle"
        headerTitle="Generar una nueva versión"
        footer={
          <div>
            <SecondaryButton
              label="Cancelar"
              onClick={() => setShowGenerateDialog(false)}
              rounded
            />
            <PrimaryButton
              rounded
              icon={`pi ${downloading ? "pi-spin pi-spinner" : "pi-check"}`}
              label="Generar"
              onClick={handleGenerateVersion}
              disabled={downloading}
            />
          </div>
        }
      >
        <span>
          Cada versión nueva que generes tomará la información más actual del
          reporte del cliente.
        </span>
      </ModalForm>
      <ClientReportCard
        title={getReportTitle()}
        collapsible={collapsible}
        headerButton={
          !hideButtons && (
            <div className="flex gap-2">
              {!canGenerateReportVersions && (
                <PrimaryButton
                  label="Descargar"
                  icon={`pi ${downloading ? "pi-spin pi-spinner" : "pi-check"}`}
                  btnType="outlined"
                  onClick={handleDownloadReport}
                  disabled={
                    sprintData?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ||
                    downloading
                  }
                />
              )}
              {canGenerateReportVersions && versions.length === 0 && (
                <PrimaryButton
                  label="Generar PDF"
                  icon={`pi ${downloading ? "pi-spin pi-spinner" : "pi-check"}`}
                  onClick={
                    (
                      period.toUpperCase() ===
                        CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.BLOCK ||
                      period.toUpperCase() ===
                        CLIENT_REPORT_GROUP_SPRINTS_GROUP_BY.CYCLE
                    ) ?
                      handleDownloadReport
                    : handleGenerateVersion
                  }
                  disabled={
                    sprintData?.state === CLIENT_SPRINT_STATES.IN_PROGRESS ||
                    downloading
                  }
                />
              )}
              {canGenerateReportVersions && versions.length > 0 && (
                <SplitButton
                  ref={splitButtonRef}
                  label={`Última versión (v${versions[0].version})`}
                  icon="pi pi-download"
                  model={adminMenu}
                  onClick={() => setShowDownloadDialog(true)}
                  onHide={() => setShowHistory(false)}
                  className="bg-primary-highlight split-button-border"
                />
              )}
              <PrimaryButton
                icon="pi pi-cog text-xl"
                onClick={(e) => overlayRef?.current?.toggle(e)}
                disabled={
                  sprintData?.state === CLIENT_SPRINT_STATES.IN_PROGRESS
                }
              />
              <OverlayPanel
                ref={overlayRef}
                className="p-2 report-overlay"
                style={{ width: "251px" }}
              >
                <div
                  className="flex flex-column gap-2"
                  style={{ minWidth: 200 }}
                >
                  {Object.entries(visibleMap).flatMap(([key, value]) => {
                    if (key in NON_TOGGLEABLE_SECTIONS) return []

                    if (typeof value === "boolean") {
                      return (
                        <div
                          key={key}
                          className="flex align-items-center gap-2"
                        >
                          <InputCheckbox
                            inputId={key}
                            checked={value}
                            onChange={() => onToggleSection(key)}
                          />
                          <label
                            htmlFor={key}
                            className="report-checkbox-label"
                          >
                            {getSectionLabel(key)}
                          </label>
                        </div>
                      )
                    }

                    return Object.entries(value).map(([subKey, subValue]) => {
                      const path = `${key}-${subKey}`
                      return (
                        <div
                          key={path}
                          className="flex align-items-center gap-2"
                        >
                          <InputCheckbox
                            inputId={path}
                            checked={subValue}
                            onChange={() => onToggleSection(path)}
                          />
                          <label
                            htmlFor={path}
                            className="report-checkbox-label"
                          >
                            {getSectionLabel(path)}
                          </label>
                        </div>
                      )
                    })
                  })}
                </div>
              </OverlayPanel>
            </div>
          )
        }
        icon={
          <img
            src={Frame}
            style={{
              width: 32,
              height: 32,
            }}
          />
        }
        className="font-cairo flex-shrink-0"
      >
        <div className="client-report-grid-3 w-full">
          <ValueWithLabelTemplate
            label="Fecha de periodo"
            value={
              <div className="flex align-items-center gap-2">
                <span className="value-with-unit sumary-sprint">
                  {getPeriodLabel(data?.startDate, data?.endDate)}
                </span>
              </div>
            }
            loading={isPresentationMode ? hookLoading : loading}
            className="min-w-0"
          />
          <ValueWithLabelTemplate
            label="Consumo del periodo"
            value={
              data?.periodConsumption ?
                <div className="flex align-items-center gap-2">
                  <span className="value-with-unit sumary-sprint">
                    {numberFormatter(data.periodConsumption, 1)}
                    <span className="unit"> horas</span>
                  </span>
                </div>
              : "-"
            }
            loading={isPresentationMode ? hookLoading : loading}
            className="min-w-0"
            labelTooltip
            labelTooltipIcon={"pi pi-info-circle"}
            labelTooltipPosition="top"
            labelTooltipContent="Horas facturadas durante el periodo del sprint"
          />
          <ValueWithLabelTemplate
            label="Avance del periodo"
            value={
              <div className="flex align-items-center gap-2">
                <span className="value-with-unit sumary-sprint">
                  {displayValueOrPlaceholder(
                    data?.periodPoints,
                    numberFormatter,
                    "0"
                  )}
                  <span className="unit"> pts dev</span>
                </span>
              </div>
            }
            loading={isPresentationMode ? hookLoading : loading}
            className="min-w-0"
          />

          {!hideResponsible && (
            <ValueWithLabelTemplate
              label="Responsable"
              value={
                data?.approvedByName ?
                  `${data.approvedByFirstName} ${data.approvedByLastName}`
                : "-"
              }
              loading={isPresentationMode ? hookLoading : loading}
              className="min-w-0"
            />
          )}
        </div>
      </ClientReportCard>
    </>
  )
}

export default ClientReportSprintSummary
